/**
 * Centralized map configuration with environment variables
 */

import { PARCEL_PAINT_MIN_ZOOM } from '../utils/parcelZoomPolicy';
import { resolveParcelSources } from '../utils/parcelSources';

export { PARCEL_PAINT_MIN_ZOOM };

// Mapbox access token from environment variable
export const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

// Legacy Regrid JWT — unused. Parcels are self-hosted Land Records MVT.
export const REGRID_TILES_TOKEN = process.env.REACT_APP_REGRID_TILES_TOKEN || '';

// Esri Reference overlay tiles (transparent labels/boundaries/roads over imagery)
export const ESRI_REFERENCE_BOUNDARIES_PLACES =
  'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}';

/** Roads/highways/rail overlay — intended under Boundaries & Places on satellite (Esri hybrid recipe). */
export const ESRI_REFERENCE_TRANSPORTATION =
  'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}';

/**
 * @deprecated Regrid is cancelled. Kept only so old imports do not crash.
 */
export function getRegridTilesUrl(token = REGRID_TILES_TOKEN) {
  if (!token) return '';
  return `https://tiles.regrid.com/api/v1/parcels/{z}/{x}/{y}.mvt?token=${token}`;
}

/** Self-hosted Land Records MVT. Local smoke or Spaces CDN. Never Regrid. */
export const PARCEL_TILES_URL = (process.env.REACT_APP_PARCEL_TILES_URL || '').trim();
export const PARCEL_TILES_URLS = (process.env.REACT_APP_PARCEL_TILES_URLS || '').trim();

export function getParcelOverlayConfig() {
  const minzoom = Number(process.env.REACT_APP_PARCEL_SOURCE_MINZOOM);
  const maxzoom = Number(process.env.REACT_APP_PARCEL_SOURCE_MAXZOOM);
  const sources = resolveParcelSources({
    tilesUrl: PARCEL_TILES_URL,
    tilesUrls: PARCEL_TILES_URLS
  });
  const resolvedMin = Number.isFinite(minzoom) ? minzoom : PARCEL_PAINT_MIN_ZOOM;
  const resolvedMax = Number.isFinite(maxzoom) ? maxzoom : 16;
  const promoteId = (process.env.REACT_APP_PARCEL_PROMOTE_ID || 'lrid').trim() || 'lrid';
  if (!sources.length) {
    return {
      kind: 'none',
      tilesUrl: '',
      sources: [],
      minzoom: resolvedMin,
      maxzoom: resolvedMax,
      promoteId
    };
  }
  return {
    kind: 'landrecords',
    tilesUrl: sources[0].tilesUrl,
    sources,
    minzoom: resolvedMin,
    maxzoom: resolvedMax,
    promoteId
  };
}

export function isLandRecordsParcelOverlay() {
  return getParcelOverlayConfig().kind === 'landrecords';
}

// Base map styles with Esri as default (more recent satellite)
export const BASE_MAP_STYLES = {
  // Esri World Imagery (more recent satellite) - our default
  satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  
  // Esri World Topo Map
  topo: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
  
  // Esri World Street Map
  street: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
  
  // Mapbox styles (fallback only)
  mapbox_standard: 'mapbox://styles/mapbox/standard',
  mapbox_satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
  mapbox_outdoors: 'mapbox://styles/mapbox/outdoors-v12',
  mapbox_streets: 'mapbox://styles/mapbox/streets-v12'
};

// Default base map style - always use Esri satellite for better imagery
export const DEFAULT_BASE_MAP = 'satellite';

/** Club maps use the same Esri satellite as personal maps */
export const CLUB_DEFAULT_BASE_MAP = DEFAULT_BASE_MAP;

// Mapbox style configuration (only used if token is available)
export const MAPBOX_STYLE_CONFIG = {
  version: 8,
  sources: {
    'esri-satellite': {
      type: 'raster',
      tiles: [BASE_MAP_STYLES.satellite],
      tileSize: 256,
      // Esri imagery caps around 18–19 in many areas; set source maxzoom to 18 for safe overscaling
      maxzoom: 18,
      attribution: '© Esri'
    },
    'esri-topo': {
      type: 'raster',
      tiles: [BASE_MAP_STYLES.topo],
      tileSize: 256,
      maxzoom: 18,
      attribution: '© Esri'
    },
    'esri-street': {
      type: 'raster',
      tiles: [BASE_MAP_STYLES.street],
      tileSize: 256,
      maxzoom: 18,
      attribution: '© Esri'
    }
  },
  layers: [
    {
      id: 'esri-satellite-layer',
      type: 'raster',
      source: 'esri-satellite',
      minzoom: 0,
      // Keep layer visible up to 22 so Mapbox upscales lower-zoom tiles
      maxzoom: 22,
      paint: {
        // Avoid long crossfades that can look like blanking when overscaling
        'raster-fade-duration': 0
      }
    }
  ]
};

/**
 * Get the appropriate map style based on configuration
 * @param {string} styleName - Name of the style to use
 * @returns {string|Object} Map style URL or configuration object
 */
export function getMapStyle(styleName = DEFAULT_BASE_MAP) {
  // If Mapbox token is available and user wants Mapbox style
  if (MAPBOX_ACCESS_TOKEN && styleName.startsWith('mapbox_')) {
    return BASE_MAP_STYLES[styleName];
  }

  // Field-edition clubhouse board underlay (georeferenced Esri topo + paper wash).
  // Prefer raster style object over Mapbox light URL so tiles always paint (no blank canvas).
  if (styleName === 'board') {
    return {
      version: 8,
      name: 'yona-board',
      sources: {
        'esri-topo': {
          type: 'raster',
          tiles: [BASE_MAP_STYLES.topo],
          tileSize: 256,
          maxzoom: 18,
          attribution: '© Esri'
        }
      },
      layers: [
        {
          id: 'board-paper',
          type: 'background',
          paint: {
            'background-color': '#C6BF9E'
          }
        },
        {
          id: 'esri-topo-layer',
          type: 'raster',
          source: 'esri-topo',
          minzoom: 0,
          maxzoom: 22,
          paint: {
            'raster-opacity': 0.72,
            'raster-saturation': -0.35,
            'raster-contrast': -0.12,
            'raster-fade-duration': 0
          }
        }
      ]
    };
  }
  
  // For Esri styles, return the proper style configuration object
  if (styleName === 'satellite') {
    return {
      version: 8,
      sources: {
        'esri-satellite': {
          type: 'raster',
          tiles: [BASE_MAP_STYLES.satellite],
          tileSize: 256,
          maxzoom: 18,
          attribution: '© Esri'
        }
      },
      layers: [
        {
          id: 'esri-satellite-layer',
          type: 'raster',
          source: 'esri-satellite',
          minzoom: 0,
          maxzoom: 22,
          paint: {
            'raster-fade-duration': 0
          }
        }
      ]
    };
  }
  
  if (styleName === 'topo') {
    return {
      version: 8,
      sources: {
        'esri-topo': {
          type: 'raster',
          tiles: [BASE_MAP_STYLES.topo],
          tileSize: 256,
          maxzoom: 18,
          attribution: '© Esri'
        }
      },
      layers: [
        {
          id: 'esri-topo-layer',
          type: 'raster',
          source: 'esri-topo',
          minzoom: 0,
          maxzoom: 22,
          paint: {
            'raster-fade-duration': 0
          }
        }
      ]
    };
  }
  
  if (styleName === 'street') {
    return {
      version: 8,
      sources: {
        'esri-street': {
          type: 'raster',
          tiles: [BASE_MAP_STYLES.street],
          tileSize: 256,
          maxzoom: 18,
          attribution: '© Esri'
        }
      },
      layers: [
        {
          id: 'esri-street-layer',
          type: 'raster',
          source: 'esri-street',
          minzoom: 0,
          maxzoom: 22,
          paint: {
            'raster-fade-duration': 0
          }
        }
      ]
    };
  }
  
  // Fallback to satellite
  return {
    version: 8,
    sources: {
      'esri-satellite': {
        type: 'raster',
        tiles: [BASE_MAP_STYLES.satellite],
        tileSize: 256,
        maxzoom: 18,
        attribution: '© Esri'
      }
    },
    layers: [
      {
        id: 'esri-satellite-layer',
        type: 'raster',
        source: 'esri-satellite',
        minzoom: 0,
        maxzoom: 22,
        paint: {
          'raster-fade-duration': 0
        }
      }
    ]
  };
}

/**
 * Initialize Mapbox with environment token
 */
export function initializeMapbox() {
  if (MAPBOX_ACCESS_TOKEN) {
    // Only set token if it's available
    const mapboxgl = require('mapbox-gl');
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
  }
}